// src/utils/claimAccess.js
//
// Quién tiene acceso a un reclamo (pedido del cliente 2026-09-24):
//   - P.A Assigned Adjuster: el PA principal (`adjusterName`, a quien le
//     corresponde la comisión) + otros PA asignados (`collaboratorAdjusters`,
//     p. ej. Luis Claudio, que en Organized aparece en casi todos).
//   - Other Adjuster Access (`otherAccess`): colaboradores agregados a mano.
//   - Los asistentes enlazados al perfil de cualquiera de los PA asignados
//     (adjusters con role 'assistant' y assistantOf: [nombres de PA]) entran
//     solos — p. ej. la hermana de Julio ve y mueve los reclamos de Julio.
//   - Attorney (`attorneyName`): por ahora solo el nombre, sin cuenta.
//
// El resultado se guarda desnormalizado en `accessNames` del reclamo, sus
// cheques y sus citas: firestore.rules solo puede autorizar una consulta de
// lista si el filtro de la consulta lo prueba (where accessNames
// array-contains <mi nombre>).

const clean = name => (typeof name === 'string' ? name.trim() : '');

const unique = names => {
  const seen = new Set();
  return names.map(clean).filter(name => {
    if (!name || seen.has(name.toLowerCase())) return false;
    seen.add(name.toLowerCase());
    return true;
  });
};

export const isAssistant = adjuster => adjuster?.role === 'assistant';

// Personal CSR (CSR Assistant): entra por compañía, no se asigna como P.A de un reclamo.
export const isCsrStaff = adjuster => adjuster?.role === 'csr_assistant';

// PA asignados al reclamo, el principal primero.
export const assignedAdjustersOf = claimRecord => unique([
  claimRecord?.adjusterName,
  ...(Array.isArray(claimRecord?.collaboratorAdjusters) ? claimRecord.collaboratorAdjusters : [])
]);

// Asistentes activos enlazados a alguno de esos PA.
export const assistantsFor = (paNames, adjusters = []) => {
  const wanted = new Set(paNames.map(name => clean(name).toLowerCase()));
  return adjusters
    .filter(a => isAssistant(a) && a.active !== false)
    .filter(a => (a.assistantOf || []).some(pa => wanted.has(clean(pa).toLowerCase())))
    .map(a => a.name);
};

export const buildAccessNames = (claimRecord, adjusters = []) => {
  const assigned = assignedAdjustersOf(claimRecord);
  return unique([
    ...assigned,
    ...(Array.isArray(claimRecord?.otherAccess) ? claimRecord.otherAccess : []),
    ...assistantsFor(assigned, adjusters)
  ]);
};

// ¿Este nombre (el del usuario logueado) tiene acceso al reclamo, cheque o cita?
export const hasAccess = (record, name) => {
  const me = clean(name).toLowerCase();
  if (!me) return false;
  if (clean(record?.adjusterName).toLowerCase() === me) return true;
  return (record?.accessNames || []).some(n => clean(n).toLowerCase() === me);
};

// Para las consultas de un PA sin permiso de ver toda la empresa: junta el
// resultado de "soy el PA principal" y "estoy en accessNames" sin repetir.
export const mergeById = (...lists) => {
  const byId = new Map();
  lists.flat().forEach(item => { if (item?.id && !byId.has(item.id)) byId.set(item.id, item); });
  return [...byId.values()];
};
